import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, 
  Lock, 
  ArrowLeft, 
  PencilSimple, 
  Check, 
  X,
  Eye,
  EyeSlash,
  ChartLine,
  CalendarBlank,
  Atom,
  Trash,
  Warning
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

export default function Profile() {
  const { currentUser, isAuthenticated, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  
  // Profile edit state
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState('');
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // History data
  const [hormoneHistory, setHormoneHistory] = useState([]);
  const [predictionHistory, setPredictionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (currentUser) {
      setName(currentUser.name || '');
    }
  }, [currentUser]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const [hormonesRes, predictionsRes] = await Promise.all([
        api.getHormoneHistory(),
        api.getPredictionHistory()
      ]);
      setHormoneHistory(hormonesRes.data.history || []);
      setPredictionHistory(predictionsRes.data.history || []);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleUpdateName = async () => {
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    
    setLoading(true);
    try {
      await api.updateProfile({ name: name.trim() });
      toast.success('Name updated successfully');
      setEditingName(false);
      // Refresh auth context
      window.location.reload();
    } catch (error) {
      toast.error('Failed to update name');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await api.changePassword({
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    if (!deletePassword) {
      toast.error('Please enter your password');
      return;
    }
    
    setDeleting(true);
    try {
      await api.deleteAccount(deletePassword, deleteConfirmText);
      toast.success('Account deleted successfully');
      logout();
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete account';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#5B21B6] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-[#64748B] hover:text-[#1E1B4B] transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">Account Settings</h1>
          </div>
          <Link to="/">
            <span className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">
              Meno<span className="text-[#EC4899]">Path</span>
            </span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-2 rounded-xl font-medium transition-all ${
              activeTab === 'profile'
                ? 'bg-[#5B21B6] text-white'
                : 'bg-white text-[#64748B] hover:bg-[#F1F5F9]'
            }`}
          >
            <User className="w-4 h-4 inline mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`px-4 py-2 rounded-xl font-medium transition-all ${
              activeTab === 'security'
                ? 'bg-[#5B21B6] text-white'
                : 'bg-white text-[#64748B] hover:bg-[#F1F5F9]'
            }`}
          >
            <Lock className="w-4 h-4 inline mr-2" />
            Security
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-xl font-medium transition-all ${
              activeTab === 'history'
                ? 'bg-[#5B21B6] text-white'
                : 'bg-white text-[#64748B] hover:bg-[#F1F5F9]'
            }`}
          >
            <ChartLine className="w-4 h-4 inline mr-2" />
            History
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-2xl p-6 border border-[#E2E8F0]">
              <h2 className="text-lg font-semibold text-[#1E1B4B] mb-4">Personal Information</h2>
              
              {/* Name */}
              <div className="flex items-center justify-between py-4 border-b border-[#E2E8F0]">
                <div>
                  <p className="text-sm text-[#64748B]">Name</p>
                  {editingName ? (
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-64 h-9"
                        autoFocus
                      />
                      <button
                        onClick={handleUpdateName}
                        disabled={loading}
                        className="p-2 text-[#10B981] hover:bg-[#10B981]/10 rounded-lg"
                      >
                        <Check className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingName(false);
                          setName(currentUser?.name || '');
                        }}
                        className="p-2 text-[#DC2626] hover:bg-[#DC2626]/10 rounded-lg"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-[#1E1B4B] font-medium">{currentUser?.name}</p>
                  )}
                </div>
                {!editingName && (
                  <button
                    onClick={() => setEditingName(true)}
                    className="p-2 text-[#64748B] hover:text-[#5B21B6] hover:bg-[#5B21B6]/10 rounded-lg transition-colors"
                  >
                    <PencilSimple className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              {/* Email */}
              <div className="py-4">
                <p className="text-sm text-[#64748B]">Email</p>
                <p className="text-[#1E1B4B] font-medium">{currentUser?.email}</p>
                <p className="text-xs text-[#94A3B8] mt-1">Email cannot be changed</p>
              </div>
            </div>

            {/* Account Info */}
            <div className="bg-white rounded-2xl p-6 border border-[#E2E8F0]">
              <h2 className="text-lg font-semibold text-[#1E1B4B] mb-4">Account</h2>
              <div className="py-4 border-b border-[#E2E8F0]">
                <p className="text-sm text-[#64748B]">Member Since</p>
                <p className="text-[#1E1B4B] font-medium">
                  {currentUser?.created_at ? formatDate(currentUser.created_at) : '—'}
                </p>
              </div>
              <div className="py-4">
                <Button
                  onClick={() => {
                    logout();
                    navigate('/');
                  }}
                  variant="outline"
                  className="border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/10"
                >
                  Sign Out
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-2xl p-6 border border-[#E2E8F0]">
              <h2 className="text-lg font-semibold text-[#1E1B4B] mb-4">Password</h2>
              
              {!showPasswordForm ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#1E1B4B]">••••••••••••</p>
                    <p className="text-sm text-[#64748B]">Last changed: Unknown</p>
                  </div>
                  <Button
                    onClick={() => setShowPasswordForm(true)}
                    variant="outline"
                    className="border-[#5B21B6] text-[#5B21B6] hover:bg-[#5B21B6]/10"
                  >
                    Change Password
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]"
                      >
                        {showCurrentPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        className="pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]"
                      >
                        {showNewPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-[#5B21B6] hover:bg-[#4C1D95]"
                    >
                      {loading ? 'Updating...' : 'Update Password'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setCurrentPassword('');
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </div>

            {/* Delete Account Section */}
            <div className="bg-white rounded-2xl p-6 border border-[#FECACA]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#FEE2E2] flex items-center justify-center">
                  <Trash className="w-5 h-5 text-[#DC2626]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#DC2626]">Delete Account</h2>
                  <p className="text-sm text-[#64748B]">Permanently remove your account and all data</p>
                </div>
              </div>
              
              {!showDeleteConfirm ? (
                <div>
                  <p className="text-sm text-[#64748B] mb-4">
                    Once you delete your account, all your data including hormone tests, lifestyle assessments, 
                    and predictions will be permanently removed. This action cannot be undone.
                  </p>
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="outline"
                    className="border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/10"
                  >
                    <Trash className="w-4 h-4 mr-2" />
                    Delete My Account
                  </Button>
                </div>
              ) : (
                <div className="space-y-4 p-4 bg-[#FEF2F2] rounded-xl border border-[#FECACA]">
                  <div className="flex items-start gap-3">
                    <Warning className="w-5 h-5 text-[#DC2626] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-[#DC2626]">Are you sure?</p>
                      <p className="text-sm text-[#991B1B]">This will permanently delete all your data.</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="deletePassword" className="text-[#991B1B]">Enter your password</Label>
                      <Input
                        id="deletePassword"
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        className="mt-1 border-[#FECACA]"
                        placeholder="Your password"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="deleteConfirm" className="text-[#991B1B]">Type DELETE to confirm</Label>
                      <Input
                        id="deleteConfirm"
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                        className="mt-1 border-[#FECACA]"
                        placeholder="DELETE"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={handleDeleteAccount}
                      disabled={deleting || deleteConfirmText !== 'DELETE' || !deletePassword}
                      className="bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50"
                    >
                      {deleting ? 'Deleting...' : 'Permanently Delete'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeletePassword('');
                        setDeleteConfirmText('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {loadingHistory ? (
              <div className="bg-white rounded-2xl p-8 border border-[#E2E8F0] text-center">
                <div className="animate-spin w-8 h-8 border-4 border-[#5B21B6] border-t-transparent rounded-full mx-auto" />
                <p className="text-[#64748B] mt-4">Loading history...</p>
              </div>
            ) : (
              <>
                {/* Hormone Test History */}
                <div className="bg-white rounded-2xl p-6 border border-[#E2E8F0]">
                  <div className="flex items-center gap-2 mb-4">
                    <Atom className="w-5 h-5 text-[#7C3AED]" />
                    <h2 className="text-lg font-semibold text-[#1E1B4B]">Hormone Test History</h2>
                    <span className="text-sm text-[#64748B]">({hormoneHistory.length} records)</span>
                  </div>
                  
                  {hormoneHistory.length === 0 ? (
                    <p className="text-[#64748B] text-sm">No hormone test records yet.</p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {hormoneHistory.map((record, index) => (
                        <div 
                          key={record.id || index} 
                          className="p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <CalendarBlank className="w-4 h-4 text-[#64748B]" />
                              <span className="font-medium text-[#1E1B4B]">
                                {formatDate(record.test_date)}
                              </span>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-sm">
                            <div>
                              <span className="text-[#64748B]">FSH:</span>
                              <span className="ml-1 text-[#1E1B4B] font-medium">{record.fsh || '—'}</span>
                            </div>
                            <div>
                              <span className="text-[#64748B]">LH:</span>
                              <span className="ml-1 text-[#1E1B4B] font-medium">{record.lh || '—'}</span>
                            </div>
                            <div>
                              <span className="text-[#64748B]">E2:</span>
                              <span className="ml-1 text-[#1E1B4B] font-medium">{record.estradiol_e2 || '—'}</span>
                            </div>
                            <div>
                              <span className="text-[#64748B]">AMH:</span>
                              <span className="ml-1 text-[#1E1B4B] font-medium">{record.amh || '—'}</span>
                            </div>
                            <div>
                              <span className="text-[#64748B]">Inhibin B:</span>
                              <span className="ml-1 text-[#1E1B4B] font-medium">{record.inhibin_b || '—'}</span>
                            </div>
                            <div>
                              <span className="text-[#64748B]">P4:</span>
                              <span className="ml-1 text-[#1E1B4B] font-medium">{record.progesterone || '—'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Prediction History */}
                <div className="bg-white rounded-2xl p-6 border border-[#E2E8F0]">
                  <div className="flex items-center gap-2 mb-4">
                    <ChartLine className="w-5 h-5 text-[#EC4899]" />
                    <h2 className="text-lg font-semibold text-[#1E1B4B]">Prediction History</h2>
                    <span className="text-sm text-[#64748B]">({predictionHistory.length} predictions)</span>
                  </div>
                  
                  {predictionHistory.length === 0 ? (
                    <p className="text-[#64748B] text-sm">No predictions yet. Run your first analysis!</p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {predictionHistory.map((record, index) => (
                        <div 
                          key={record.id || index} 
                          className="p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <CalendarBlank className="w-4 h-4 text-[#64748B]" />
                              <span className="text-sm text-[#64748B]">
                                {formatDate(record.created_at)}
                              </span>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              record.confidence === 'high' 
                                ? 'bg-[#DCFCE7] text-[#166534]'
                                : record.confidence === 'medium'
                                  ? 'bg-[#FEF3C7] text-[#92400E]'
                                  : 'bg-[#F1F5F9] text-[#64748B]'
                            }`}>
                              {record.confidence} confidence
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            <div>
                              <p className="text-xs text-[#64748B]">Predicted Age</p>
                              <p className="text-xl font-bold text-[#EC4899]">
                                {record.predicted_age_range}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-[#64748B]">Stage</p>
                              <p className="font-medium text-[#1E1B4B] capitalize">
                                {record.stage?.replace(/_/g, ' ') || '—'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
}
